import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  Briefcase,
  Building,
  Home,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  MapPin,
  Calendar,
  Loader2,
  AlertCircle,
  FileText,
  ShoppingBag,
  Tag
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const MySubmissionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchSubmissions();
  }, [user, navigate]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const [jobsRes, businessesRes, propertiesRes, listingsRes] = await Promise.all([
        axios.get(`${API}/api/jobs/user/my-jobs`),
        axios.get(`${API}/api/businesses/user/my-businesses`),
        axios.get(`${API}/api/housing/user/my-properties`),
        axios.get(`${API}/api/marketplace/user/my-listings`)
      ]);
      setJobs(jobsRes.data || []);
      setBusinesses(businessesRes.data || []);
      setProperties(propertiesRes.data || []);
      setListings(listingsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" />
            Pending Review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job listing?')) return;
    try {
      await axios.delete(`${API}/api/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business listing?')) return;
    try {
      await axios.delete(`${API}/api/businesses/${businessId}`);
      toast.success('Business deleted successfully');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property listing?')) return;
    try {
      await axios.delete(`${API}/api/housing/${propertyId}`);
      toast.success('Property deleted successfully');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this marketplace listing?')) return;
    try {
      await axios.delete(`${API}/api/marketplace/${listingId}`);
      toast.success('Listing deleted successfully');
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  if (!user) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black mb-2">My Submissions</h1>
            <p className="text-muted-foreground">
              Manage your listings, job posts, businesses, and properties
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Tabs defaultValue="jobs" className="space-y-6">
              <TabsList className="bg-white border-2 border-border p-1 rounded-xl w-full justify-start flex-wrap">
                <TabsTrigger value="jobs" className="rounded-lg" data-testid="my-jobs-tab">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Jobs ({jobs.length})
                </TabsTrigger>
                <TabsTrigger value="businesses" className="rounded-lg" data-testid="my-businesses-tab">
                  <Building className="w-4 h-4 mr-2" />
                  Businesses ({businesses.length})
                </TabsTrigger>
                <TabsTrigger value="properties" className="rounded-lg" data-testid="my-properties-tab">
                  <Home className="w-4 h-4 mr-2" />
                  Properties ({properties.length})
                </TabsTrigger>
                <TabsTrigger value="marketplace" className="rounded-lg" data-testid="my-listings-tab">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Marketplace ({listings.length})
                </TabsTrigger>
              </TabsList>

              {/* Jobs Tab */}
              <TabsContent value="jobs" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">My Job Listings</h2>
                  <Link to="/jobs/create">
                    <Button className="rounded-xl font-bold" data-testid="create-job-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Post New Job
                    </Button>
                  </Link>
                </div>

                {jobs.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Job Listings Yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Start by posting your first job listing
                    </p>
                    <Link to="/jobs/create">
                      <Button className="rounded-xl font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        Post Your First Job
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {jobs.map(job => (
                      <div key={job.job_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusBadge(job.moderation_status)}
                              <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                                {job.category}
                              </span>
                              <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
                                {job.job_type}
                              </span>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{job.title}</h3>
                            <p className="text-sm text-muted-foreground mb-2">{job.company_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin className="w-4 h-4" />
                              <span>{job.city}, {job.country}</span>
                              <span className="mx-2">•</span>
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(job.created_at).toLocaleDateString()}</span>
                            </div>
                            {job.moderation_status === 'rejected' && job.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                                    <p className="text-sm text-red-700">{job.rejection_reason}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {job.moderation_status === 'approved' && (
                              <Link to={`/jobs/${job.job_id}`}>
                                <Button variant="outline" size="sm" className="rounded-lg">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="rounded-lg text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteJob(job.job_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Businesses Tab */}
              <TabsContent value="businesses" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">My Business Listings</h2>
                  <Link to="/businesses/register">
                    <Button className="rounded-xl font-bold" data-testid="register-business-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Register Business
                    </Button>
                  </Link>
                </div>

                {businesses.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Building className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Business Listings Yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Register your business to reach the expat community
                    </p>
                    <Link to="/businesses/register">
                      <Button className="rounded-xl font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        Register Your Business
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {businesses.map(business => (
                      <div key={business.business_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusBadge(business.moderation_status)}
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                {business.category}
                              </span>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{business.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <MapPin className="w-4 h-4" />
                              <span>{business.city}, {business.country}</span>
                              <span className="mx-2">•</span>
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(business.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{business.description}</p>
                            {business.moderation_status === 'rejected' && business.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                                    <p className="text-sm text-red-700">{business.rejection_reason}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {business.moderation_status === 'approved' && (
                              <Link to={`/businesses/${business.business_id}`}>
                                <Button variant="outline" size="sm" className="rounded-lg">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="rounded-lg text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteBusiness(business.business_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Properties Tab */}
              <TabsContent value="properties" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">My Property Listings</h2>
                  <Link to="/housing/create">
                    <Button className="rounded-xl font-bold" data-testid="list-property-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      List Property
                    </Button>
                  </Link>
                </div>

                {properties.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Property Listings Yet</h3>
                    <p className="text-muted-foreground mb-6">
                      List your property to reach expats looking for housing
                    </p>
                    <Link to="/housing/create">
                      <Button className="rounded-xl font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        List Your First Property
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {properties.map(property => (
                      <div key={property.property_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusBadge(property.moderation_status)}
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                {property.listing_type === 'sale' ? 'For Sale' : property.listing_type === 'short-term' ? 'Short-term' : 'For Rent'}
                              </span>
                              <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
                                {property.property_type}
                              </span>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{property.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <MapPin className="w-4 h-4" />
                              <span>
                                {property.district && `${property.district}, `}{property.city}, {property.country}
                              </span>
                              <span className="mx-2">•</span>
                              <span className="font-bold text-primary">
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: property.price_currency || 'USD', maximumFractionDigits: 0 }).format(property.price)}
                                {property.listing_type !== 'sale' && `/${property.price_period || 'month'}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>{property.bedrooms} bed</span>
                              <span>{property.bathrooms} bath</span>
                              {property.size_sqm && <span>{property.size_sqm} sqm</span>}
                            </div>
                            {property.moderation_status === 'rejected' && property.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                                    <p className="text-sm text-red-700">{property.rejection_reason}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {property.moderation_status === 'approved' && (
                              <Link to={`/housing/${property.property_id}`}>
                                <Button variant="outline" size="sm" className="rounded-lg">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="rounded-lg text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteProperty(property.property_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Marketplace Tab */}
              <TabsContent value="marketplace" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">My Marketplace Listings</h2>
                  <Link to="/marketplace/create">
                    <Button className="rounded-xl font-bold" data-testid="create-listing-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Sell Something
                    </Button>
                  </Link>
                </div>

                {listings.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">No Marketplace Listings Yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Sell or give away items you no longer need
                    </p>
                    <Link to="/marketplace/create">
                      <Button className="rounded-xl font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        Sell Something
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {listings.map(listing => (
                      <div key={listing.listing_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusBadge(listing.moderation_status)}
                              <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">
                                {listing.category}
                              </span>
                              <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
                                {listing.condition}
                              </span>
                              {listing.is_sold && (
                                <span className="px-2 py-1 bg-gray-800 text-white rounded-full text-xs font-medium">
                                  SOLD
                                </span>
                              )}
                            </div>
                            <h3 className="font-bold text-lg mb-1">{listing.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <MapPin className="w-4 h-4" />
                              <span>{listing.city}, {listing.country}</span>
                              <span className="mx-2">•</span>
                              <span className="font-bold text-pink-600">
                                {listing.is_free ? 'FREE' : new Intl.NumberFormat('en-US', { style: 'currency', currency: listing.price_currency || 'USD', maximumFractionDigits: 0 }).format(listing.price)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{listing.description}</p>
                            {listing.moderation_status === 'rejected' && listing.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                                    <p className="text-sm text-red-700">{listing.rejection_reason}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {listing.moderation_status === 'approved' && (
                              <Link to={`/marketplace/${listing.listing_id}`}>
                                <Button variant="outline" size="sm" className="rounded-lg">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="rounded-lg text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteListing(listing.listing_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MySubmissionsPage;
